import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  OnChanges,
  Optional,
  Self,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  Renderer2,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValuesOf,
} from '@ngneat/reactive-forms';
import _ from 'lodash';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { Attachment } from '@digital-platform/attachments/domain';
import {
  AttachmentUploadFacadeService,
  AttachmentValidatorService,
} from '@digital-platform/attachments/services';
import {
  FileUploadService,
  FileValidatorService,
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';
import {
  RegulatoryQuestion,
  RegulatoryQuestionsModel,
  SpecialWasteProfile,
} from '@digital-platform/waste-profiles/domain';

type RegulatoryQuestionModel = SpecialWasteProfile['regulatoryQuestions'];

interface RegulatoryQuestionControls {
  id: FormControl<RegulatoryQuestion['id']>;
  questionText: FormControl<string>;
  response: FormControl<string>;
  type?: FormControl<RegulatoryQuestion['type']>;
  alreadyUploaded?: FormControl<boolean>;
  attachment?: FormControl<Attachment>;
  applicableWasteCodesAndAgents?: FormControl<string>;
  regulatorCitation?: FormControl<string>;
  description?: FormControl<string>;
}

interface FormControls {
  regulatoryQuestions: FormArray<
    RegulatoryQuestion,
    FormGroup<RegulatoryQuestionControls>
  >;
  rsHandlingQuestions: FormArray<
    RegulatoryQuestion,
    FormGroup<RegulatoryQuestionControls>
  >;
}

@Component({
  selector: 'digital-platform-regulatory-questions',
  templateUrl: './regulatory-questions.component.html',
  styleUrls: ['./regulatory-questions.component.scss'],
  providers: [
    { provide: FileUploadService, useClass: AttachmentUploadFacadeService },
    { provide: FileValidatorService, useClass: AttachmentValidatorService },
  ],
})
export class RegulatoryQuestionsComponent
  extends NestedFormComponent<RegulatoryQuestionModel, FormControls>
  implements
    OnInit,
    OnChanges,
    OnDestroy,
    ControlValueAccessor<RegulatoryQuestionModel>
{
  @HostBinding() class = 'digital-platform-regulatory-questions';

  public form: FormGroup<FormControls>;
  public alreadyUploadedError: boolean;
  public enableQuestion6Error: boolean;
  @Input() profileStatus: string;
  @Input() isProfileMatched = false;
  @Input() isLegacy = false;
  @ViewChild('regulatoryRadioRef', { read: ElementRef })
  regulatoryRadioRef!: ElementRef;
  @Output() updatedTabIndex = new EventEmitter<number>();
  public tabIndex: number;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private fb: FormBuilder,
    private renderer: Renderer2
  ) {
    super(controlDir);
  }

  writeValue(value: RegulatoryQuestionModel): void {
    let initValue: RegulatoryQuestionsModel;

    // If profile matched and questions exist, clone the provided value
    if (
      this.isProfileMatched &&
      (value?.regulatoryQuestions?.length || value?.rsHandlingQuestions?.length)
    ) {
      initValue = _.cloneDeep(value);
    } else {
      // Otherwise, use default and reset all relevant fields
      const resetRegulatoryQuestions =
        RegulatoryQuestionsModel.getDefaultRegulatoryQuestions().map((q) => ({
          ...q,
          response: null,
          alreadyUploaded: null,
          attachment: null,
          applicableWasteCodesAndAgents: '',
          regulatorCitation: '',
          description: '',
        }));

      const resetRsHandlingQuestions =
        RegulatoryQuestionsModel.getDefaultRsHandlingQuestions().map((q) => ({
          ...q,
          response: null,
          attachment: null,
          description: '',
        }));

      initValue = new RegulatoryQuestionsModel({
        regulatoryQuestions: resetRegulatoryQuestions,
        rsHandlingQuestions: resetRsHandlingQuestions,
      });
    }

    // If form exists, reset arrays and rebuild with initValue
    if (this.form) {
      this.regulatoryQuestions.clear();
      this.rsHandlingQuestions.clear();

      initValue.regulatoryQuestions.forEach((regulatoryQuestion) => {
        this.regulatoryQuestions.push(
          this.createQuestionForm(regulatoryQuestion)
        );
      });

      initValue.rsHandlingQuestions.forEach((rsHandlingQuestion) => {
        this.rsHandlingQuestions.push(
          this.createQuestionForm(rsHandlingQuestion)
        );
      });

      this.form.patchValue(initValue, { emitEvent: false });
    } else {
      this.buildForm(initValue);
    }

    super.writeValue(initValue);
  }

  private buildForm(value: RegulatoryQuestionModel) {
    this.form = this.fb.group({
      regulatoryQuestions: this.fb.array(
        value.regulatoryQuestions.map((regulatoryQuestion) =>
          this.createQuestionForm(regulatoryQuestion)
        )
      ),
      rsHandlingQuestions: this.fb.array(
        value.rsHandlingQuestions.map((rsHandlingQuestion) =>
          this.createQuestionForm(rsHandlingQuestion)
        )
      ),
    });

    // update conditional validation for RCRA5
    const rcra5Group = this.getControlGroupForQuestion('rcra5');
    const rcra5ResponseControl = rcra5Group.get('response');
    const rcra5AlreadyUploadedControl = rcra5Group.get('alreadyUploaded');
    const rcra5AttachmentControl = rcra5Group.get('attachment');
    rcra5ResponseControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((response) => {
        if (response === 'no') {
          rcra5AlreadyUploadedControl.setValue(null, { emitEvent: false });
          rcra5AttachmentControl.setValue(null, { emitEvent: false });
        }
        rcra5AlreadyUploadedControl.updateValueAndValidity();
        rcra5AttachmentControl.updateValueAndValidity();
      });
    rcra5AlreadyUploadedControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((response) => {
        if (response === true) {
          rcra5AttachmentControl.setValue(null, { emitEvent: false });
        }
        rcra5AttachmentControl.updateValueAndValidity();
      });
    rcra5AttachmentControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        rcra5AlreadyUploadedControl.updateValueAndValidity();
      });

    // update conditional validation for RCRA6
    const rcra6ResponseControl =
      this.getControlGroupForQuestion('rcra6').get('response');
    const rcra6ApplicableWasteCodesAndAgentsControl =
      this.getControlGroupForQuestion('rcra6').get(
        'applicableWasteCodesAndAgents'
      );
    rcra6ResponseControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((response) => {
        if (response === 'no') {
          rcra6ApplicableWasteCodesAndAgentsControl.setValue('', {
            emitEvent: false,
          });
        }
        rcra6ApplicableWasteCodesAndAgentsControl.updateValueAndValidity();
      });

    // update conditional validation for RCRA10
    const rcra10ResponseControl =
      this.getControlGroupForQuestion('rcra10').get('response');
    const rcra10RegulatorCitationControl =
      this.getControlGroupForQuestion('rcra10').get('regulatorCitation');
    rcra10ResponseControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((response) => {
        if (response === 'no') {
          rcra10RegulatorCitationControl.setValue('', {
            emitEvent: false,
          });
        }
        rcra10RegulatorCitationControl.updateValueAndValidity();
      });

    // update conditional validation for REP4
    const rep4Group = this.getControlGroupForQuestion('rep4');
    const rep4ResponseControl = rep4Group.get('response');
    const rep4DescriptionControl = rep4Group.get('description');
    const rep4AttachmentControl = rep4Group.get('attachment');
    rep4ResponseControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((response) => {
        if (response === 'no') {
          rep4DescriptionControl.setValue(null, { emitEvent: false });
          rep4AttachmentControl.setValue(null, { emitEvent: false });
        }
        rep4DescriptionControl.updateValueAndValidity();
        rep4AttachmentControl.updateValueAndValidity();
      });
    rep4DescriptionControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        rep4AttachmentControl.updateValueAndValidity();
      });
    rep4AttachmentControl.valueChanges
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        rep4DescriptionControl.updateValueAndValidity();
      });
  }

  public get regulatoryQuestions(): FormArray<
    RegulatoryQuestion,
    FormGroup<RegulatoryQuestionControls>
  > {
    return this.form.get('regulatoryQuestions');
  }

  public get rsHandlingQuestions(): FormArray<
    RegulatoryQuestion,
    FormGroup<RegulatoryQuestionControls>
  > {
    return this.form.get('rsHandlingQuestions');
  }

  private createQuestionForm(
    question: RegulatoryQuestion
  ): FormGroup<RegulatoryQuestionControls> {
    const questionForm = {
      id: question.id,
      questionText: question.questionText,
      response: !this.isLegacy
        ? this.fb.control(question.response, [Validators.required])
        : this.fb.control(question.response),
    };

    switch (question?.type) {
      case 'delisted':
        return this.fb.group({
          ...questionForm,
          type: question.type,
          alreadyUploaded: [
            question.alreadyUploaded,

            FormValidators.requiredTrueIf(() => {
              const group = this.getControlGroupForQuestion(question.id);
              const responseControl = group.get('response');
              const attachmentControl = group.get('attachment');

              return (
                responseControl.value === 'yes' &&
                attachmentControl.value === null
              );
            }),
          ],
          attachment: [
            question.attachment,

            FormValidators.requiredIf(() => {
              const group = this.getControlGroupForQuestion(question.id);
              const responseControl = group.get('response');
              const alreadyUploadedControl = group.get('alreadyUploaded');

              return (
                responseControl.value === 'yes' &&
                alreadyUploadedControl.value !== true
              );
            }),
          ],
        });
      case 'hazardousCharacteristics':
        return this.fb.group({
          ...questionForm,
          type: question.type,
          applicableWasteCodesAndAgents: [
            question.applicableWasteCodesAndAgents,
            [
              Validators.maxLength(100),
              FormValidators.requiredIf(() => {
                const responseControl = this.getControlGroupForQuestion(
                  question.id
                ).get('response');

                return responseControl.value === 'yes';
              }),
            ],
          ],
        });
      case 'hazardousRegulatoryCitation':
        return this.fb.group({
          ...questionForm,
          type: question.type,
          regulatorCitation: [
            question.regulatorCitation,
            [
              Validators.maxLength(100),
              FormValidators.requiredIf(() => {
                const responseControl = this.getControlGroupForQuestion(
                  question.id
                ).get('response');

                return responseControl.value === 'yes';
              }),
            ],
          ],
        });
      case 'tsdLike':
        return this.fb.group({
          ...questionForm,
          type: question.type,
          description: [
            question.description,
            FormValidators.requiredIf(() => {
              const group = this.getControlGroupForQuestion(question.id);
              const responseControl = group.get('response');
              const attachmentControl = group.get('attachment');

              return (
                responseControl.value === 'yes' &&
                attachmentControl.value === null
              );
            }),
          ],
          attachment: [
            question.attachment,

            FormValidators.requiredIf(() => {
              const group = this.getControlGroupForQuestion(question.id);
              const responseControl = group.get('response');
              const descriptionControl = group.get('description');

              return (
                responseControl.value === 'yes' && !descriptionControl.value
              );
            }),
          ],
        });

      default:
        return this.fb.group({
          ...questionForm,
        });
    }
  }

  hasFormControlError = (
    id: RegulatoryQuestion['id'],
    control: keyof RegulatoryQuestion,
    error: string
  ) => {
    const formControl = this.getControlGroupForQuestion(id).get(control);

    if (formControl && formControl.touched && formControl.hasError(error)) {
      return true;
    } else {
      return false;
    }
  };

  private getControlGroupForQuestion(
    id: string
  ): FormGroup<RegulatoryQuestionControls> {
    return [
      ...this.regulatoryQuestions.controls,
      ...this.rsHandlingQuestions.controls,
    ].find((control) => control.value.id === id);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      this.rsHandlingQuestions.controls.forEach((control) => {
        control.controls.response.removeValidators(Validators.required);
      });
      this.regulatoryQuestions.controls.forEach((control) => {
        control.controls.response.removeValidators(Validators.required);
      });
    } else if (
      !this.isLegacy &&
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      this.rsHandlingQuestions.controls.forEach((control) => {
        control.controls.response.addValidators(Validators.required);
      });
      this.regulatoryQuestions.controls.forEach((control) => {
        control.controls.response.addValidators(Validators.required);
      });
    }
  }

  onRegulatoryLastTabIndexChange(
    event: KeyboardEvent,
    index: number,
    value: string,
    type: string
  ) {
    if (
      (value === 'no' &&
        event.key === 'Tab' &&
        index === this.rsHandlingQuestions.controls.length - 1) ||
      type === 'fileAttachment'
    ) {
      this.updatedTabIndex.emit(
        this.tabIndex +
          1 +
          this.regulatoryQuestions.controls.length +
          this.rsHandlingQuestions.controls.length
      );
    }
  }

  focus(tabIndex: number) {
    this.tabIndex = tabIndex;
    this.renderer.setAttribute(
      this.regulatoryRadioRef.nativeElement,
      'tabindex',
      tabIndex.toString()
    );
  }
}
